import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import Dashboard from './pages/Dashboard';
import LoyaltyPlans from './pages/LoyaltyPlans';
import Transactions from './pages/Transactions';
import Clients from './pages/Clients';
import Employees from './pages/Employees';
import Integrations from './pages/Integrations';
import Gamification from './pages/Gamification';
import Profile from './pages/Profile';
import AddRestaurant from './pages/AddRestaurant';
import Login from './pages/Login';
import Register from './pages/Register';
import SelectRestaurant, { Restaurant as RestaurantType } from './pages/SelectRestaurant';
import Layout from './components/Layout';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import './App.css';

import { SubscriptionStatus } from './types';

interface User {
  userId: string;
  phone: string;
  restaurantId: string;
  restaurantName: string;
  username?: string;
  role?: string;
  subscriptionStatus?: SubscriptionStatus;
  registrationDate?: string;
  trialDaysRemaining?: number;
}

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [restaurants, setRestaurants] = useState<RestaurantType[] | null>(null);
  const [showSelectRestaurant, setShowSelectRestaurant] = useState(false);

  useEffect(() => {
    // Verificar se há token salvo no localStorage
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    const savedRestaurants = localStorage.getItem('restaurants');
    const selectedRestaurantId = localStorage.getItem('selectedRestaurantId');
    
    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Verificar se há restaurantes salvos e se há um selecionado
      if (savedRestaurants) {
        const parsedRestaurants = JSON.parse(savedRestaurants);
        setRestaurants(parsedRestaurants);
        
        // Se houver múltiplos restaurantes e nenhum selecionado, mostrar seleção
        if (parsedRestaurants.length > 1 && !selectedRestaurantId) {
          setShowSelectRestaurant(true);
        }
        // Se houver um restaurante selecionado, atualizar userData
        else if (selectedRestaurantId && parsedRestaurants.length > 0) {
          const selectedRestaurant = parsedRestaurants.find((r: RestaurantType) => r.restaurantId === selectedRestaurantId);
          if (selectedRestaurant) {
            const updatedUser = {
              ...parsedUser,
              restaurantId: selectedRestaurant.restaurantId,
              restaurantName: selectedRestaurant.name
            };
            setUser(updatedUser);
            localStorage.setItem('userData', JSON.stringify(updatedUser));
          }
        }
      }
    }
    setLoading(false);
    
    // Listener para links de navegação
    const handleHashChange = () => {
      if (window.location.hash === '#register') {
        setShowRegister(true);
      } else if (window.location.hash === '#login') {
        setShowRegister(false);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // Verifica o hash inicial

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleLogin = (token: string, userData: User, restaurantsList?: RestaurantType[]) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    
    // Sempre definir o user primeiro para que o componente saiba que o login foi bem-sucedido
    setUser(userData);
    
    // Se houver múltiplos restaurantes, mostrar seleção
    if (restaurantsList && restaurantsList.length > 1) {
      localStorage.setItem('restaurants', JSON.stringify(restaurantsList));
      setRestaurants(restaurantsList);
      setShowSelectRestaurant(true);
      setShowRegister(false);
      window.location.hash = '';
    } else {
      // Se houver apenas um restaurante ou nenhum na lista, usar o do userData
      if (restaurantsList && restaurantsList.length === 1) {
        const singleRestaurant = restaurantsList[0];
        localStorage.setItem('selectedRestaurantId', singleRestaurant.restaurantId);
        localStorage.setItem('restaurants', JSON.stringify(restaurantsList));
        const updatedUser = {
          ...userData,
          restaurantId: singleRestaurant.restaurantId,
          restaurantName: singleRestaurant.name,
          subscriptionStatus: singleRestaurant.subscriptionStatus,
          registrationDate: singleRestaurant.registrationDate,
          trialDaysRemaining: singleRestaurant.trialDaysRemaining
        };
        setUser(updatedUser);
        localStorage.setItem('userData', JSON.stringify(updatedUser));
      } else {
        // Se não houver lista, usar o restaurantId do userData
        if (userData.restaurantId) {
          localStorage.setItem('selectedRestaurantId', userData.restaurantId);
        }
      }
      setShowSelectRestaurant(false);
      setShowRegister(false);
      window.location.hash = '';
    }
  };

  const handleRestaurantSelect = (restaurant: RestaurantType) => {
    localStorage.setItem('selectedRestaurantId', restaurant.restaurantId);
    const updatedUser = {
      ...user!,
      restaurantId: restaurant.restaurantId,
      restaurantName: restaurant.name
    };
    setUser(updatedUser);
    localStorage.setItem('userData', JSON.stringify(updatedUser));
    setShowSelectRestaurant(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('selectedRestaurantId');
    localStorage.removeItem('restaurants');
    setUser(null);
    setRestaurants(null);
    setShowSelectRestaurant(false);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontSize: '18px',
        fontWeight: 600
      }}>
        Carregando...
      </div>
    );
  }

  if (!user) {
    if (showRegister) {
      return (
        <Register 
          onRegister={handleLogin}
          onBackToLogin={() => {
            setShowRegister(false);
            window.location.hash = '';
          }}
        />
      );
    }
    return <Login onLogin={handleLogin} />;
  }

  // Se há múltiplos restaurantes e nenhum foi selecionado ainda, mostrar seleção
  if (showSelectRestaurant && restaurants && restaurants.length > 1) {
    return <SelectRestaurant restaurants={restaurants} onSelect={handleRestaurantSelect} />;
  }

  const userRole = user.role || 'admin'; // Default para admin se não especificado
  const defaultRoute = userRole === 'employee' ? '/transactions' : '/';

  return (
    <SubscriptionProvider>
      <Router>
        <Layout 
          user={user as any} 
          signOut={handleLogout}
        >
          <Routes>
            {userRole === 'admin' && (
              <>
                <Route path="/" element={<Dashboard />} />
                <Route path="/plans" element={<LoyaltyPlans />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/gamification" element={<Gamification />} />
                <Route path="/employees" element={<Employees />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/add-restaurant" element={<AddRestaurant />} />
              </>
            )}
            <Route path="/transactions" element={<Transactions />} />
            <Route path="*" element={<Navigate to={defaultRoute} replace />} />
          </Routes>
        </Layout>
      </Router>
    </SubscriptionProvider>
  );
};

export default App;

